import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import {
  formatLakh, GATE_LABELS, GATES, TEMPERATURE_COLORS, HEALTH_COLORS, formatDate, MONEY_UNIT_SHORT,
} from "@/lib/utils-ros";
import { ChevronRight, Filter, Trash2, X, Table2, Funnel as FunnelIcon } from "lucide-react";
import { PageToolbar } from "@/components/revenueos/PageToolbar";
import { PartnerBenchmarks } from "@/components/revenueos/PartnerBenchmarks";
import { FunnelBoard } from "@/components/revenueos/FunnelBoard";
import { RequestDeletionModal } from "@/components/revenueos/RequestDeletionModal";
import { ParkOpportunityModal } from "@/components/revenueos/ParkOpportunityModal";
import { toast } from "sonner";
import { canonicalRole, canEditOpportunities, useAuth } from "@/lib/auth.jsx";

// Full table: Customer / Industry / Products / Sales stage / Deal value / Probability /
// Deal health / Buyer engagement / Partner+Close / actions.
const GRID_COLS = "grid-cols-[minmax(155px,1.3fr)_75px_minmax(135px,1.1fr)_80px_58px_62px_62px_82px_minmax(115px,0.9fr)_26px]";

function titleCase(value) {
  return String(value || "—").replaceAll("_", " ").replace(/\b\w/g, char => char.toUpperCase());
}

function OpportunityFilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-[#455A64] mb-1.5">{label}</span>
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        data-testid={testId}
        className="w-full h-10 rounded-xl border border-[#B8D3DF] bg-[#F8FCFE] px-3 text-[12px] outline-none focus:border-[#00A2D4]"
      >
        {options.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
      </select>
    </label>
  );
}

export default function Opportunities() {
  const { user } = useAuth();
  const role = canonicalRole(user?.role);
  const isPartner = role === "partner";
  const isAdmin = role === "super_admin" || role === "admin" || role === "founder";
  const [deletionTarget, setDeletionTarget] = useState(null);
  const [parkTarget, setParkTarget] = useState(null);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Backed by ?view= so the Funnel toggle survives a round trip through the detail
  // page's "Back to Opportunities" link (which just navigates to this same path+query).
  const [viewMode, setViewModeState] = useState(searchParams.get("view") === "funnel" ? "funnel" : "table");
  const [moving, setMoving] = useState(null);
  const [gateFilter, setGateFilter] = useState("all");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [healthFilter, setHealthFilter] = useState("all");
  const [temperatureFilter, setTemperatureFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [loadError, setLoadError] = useState(null);
  const nav = useNavigate();
  const canEditOpp = canEditOpportunities(user);

  function setViewMode(mode) {
    setViewModeState(mode);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (mode === "funnel") next.set("view", "funnel"); else next.delete("view");
      return next;
    }, { replace: true });
  }

  async function loadOpps() {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get("/opportunities");
      setOpps(data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setLoadError(typeof detail === "string" ? detail : "Could not load opportunities.");
      setOpps([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadOpps(); }, []);

  async function deleteOpp(oid, name, event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!window.confirm(`Delete opportunity "${name}"?\n\nIt will be moved to Lost. You can restore it using the Lost button on the right side of the pipeline bar. Any existing orders remain intact.`)) return;
    try {
      await api.delete(`/opportunities/${oid}?reason=user_deleted`);
      toast.success("Moved to Lost · restore from the pipeline bar");
      setOpps(current => current.filter(opportunity => opportunity.id !== oid));
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Delete failed");
    }
  }

  // Only Super Admin deletes directly — every other role raises a deletion request
  // that lands in the Admin/Founder Requests queue instead.
  function handleDeleteClick(opp, event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (isAdmin) {
      deleteOpp(opp.id, opp.company?.name || opp.name, event);
    } else {
      setDeletionTarget(opp);
    }
  }

  async function moveGate(id, gate) {
    setMoving(id);
    try {
      await api.patch(`/opportunities/${id}`, { current_gate: gate });
      toast.success(`Moved to ${GATE_LABELS[gate]}`);
      await loadOpps();
    } catch {
      toast.error("Could not move deal — check permissions and try again");
    } finally { setMoving(null); }
  }

  async function parkOpp(id, status) {
    setMoving(id);
    try {
      await api.patch(`/opportunities/${id}`, { status });
      toast.success(status === "parked" ? "Parked" : "Reactivated");
      await loadOpps();
    } catch {
      toast.error("Update failed");
    } finally { setMoving(null); }
  }

  const industries = [...new Set(opps.map(opp => opp.company?.industry).filter(Boolean))].sort();
  const partners = [...new Set(opps.map(opp => opp.partner?.firm).filter(Boolean))].sort();

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return opps.filter(opp => {
      if (gateFilter !== "all" && opp.current_gate !== gateFilter) return false;
      if (industryFilter !== "all" && opp.company?.industry !== industryFilter) return false;
      if (healthFilter !== "all" && opp.health !== healthFilter) return false;
      if (temperatureFilter !== "all" && opp.temperature !== temperatureFilter) return false;
      if (partnerFilter === "assigned" && !opp.partner?.firm) return false;
      if (partnerFilter === "unassigned" && opp.partner?.firm) return false;
      if (!["all", "assigned", "unassigned"].includes(partnerFilter) && opp.partner?.firm !== partnerFilter) return false;
      if (normalizedQuery) {
        const searchable = [
          opp.name, opp.company?.name, opp.company?.industry, opp.partner?.firm,
          GATE_LABELS[opp.current_gate], ...(opp.products || []),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!searchable.includes(normalizedQuery)) return false;
      }
      return true;
    });
  }, [opps, query, gateFilter, industryFilter, healthFilter, temperatureFilter, partnerFilter]);

  const totalValue = filtered.reduce((sum, opp) => sum + (opp.value_cr || 0), 0);
  const weightedValue = filtered.reduce((sum, opp) => sum + (opp.value_cr || 0) * (opp.probability || 0) / 100, 0);
  const activeFilterCount = [
    gateFilter !== "all", industryFilter !== "all", healthFilter !== "all",
    temperatureFilter !== "all", partnerFilter !== "all",
  ].filter(Boolean).length;

  function clearFilters() {
    setGateFilter("all");
    setIndustryFilter("all");
    setHealthFilter("all");
    setTemperatureFilter("all");
    setPartnerFilter("all");
  }

  return (
    <div className="min-h-full bg-[#F7FAFC] pb-12" data-testid="opportunities-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-6 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#FFFFFF]">Opportunities</h1>
            <p className="text-[13px] text-white/75 mt-1">Every active commercial pursuit, from first qualification to close.</p>
          </div>
          <div className="flex items-start gap-6 md:gap-10 text-right shrink-0">
            <div>
              <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/60">Total pipeline</div>
              <div className="font-display text-xl md:text-2xl tabular-nums !text-white mt-1" data-testid="opportunities-pipeline-value">{formatLakh(totalValue)}</div>
            </div>
            <div>
              <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/60">Weighted</div>
              <div className="font-display text-xl md:text-2xl tabular-nums !text-[#F6A63B] mt-1" data-testid="opportunities-weighted-value">{formatLakh(weightedValue)}</div>
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
        <PageToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search opportunities by customer, deal, product, or partner…"
          entityLabel="opportunities"
          count={filtered.length}
          variant="universe"
          right={(
            <div className="flex items-center gap-2 ml-auto">
              <div className="flex items-center h-9 rounded-lg border border-[#BFD6E1] bg-white p-0.5 shadow-[0_4px_12px_rgba(7,83,154,0.06)]" data-testid="opportunities-view-toggle">
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  data-testid="opportunities-view-table"
                  className={`h-full px-3 rounded-md text-[12px] font-medium flex items-center gap-1.5 transition-colors ${viewMode === "table" ? "bg-[#07539A] !text-white" : "text-[#243B53] hover:text-[#07539A]"}`}
                >
                  <Table2 size={13} /> Table
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("funnel")}
                  data-testid="opportunities-view-funnel"
                  className={`h-full px-3 rounded-md text-[12px] font-medium flex items-center gap-1.5 transition-colors ${viewMode === "funnel" ? "bg-[#07539A] !text-white" : "text-[#243B53] hover:text-[#07539A]"}`}
                >
                  <FunnelIcon size={13} /> Funnel
                </button>
              </div>
              <div className="relative">
              <button
                type="button"
                onClick={() => setFilterOpen(open => !open)}
                data-testid="opportunities-filter-btn"
                className={`h-9 px-3 rounded-lg border text-[12px] font-medium flex items-center gap-2 shadow-[0_4px_12px_rgba(7,83,154,0.06)] transition-colors ${filterOpen || activeFilterCount ? "border-[#07539A] bg-[#E3F2F8] text-[#07539A]" : "border-[#BFD6E1] bg-white text-[#243B53] hover:border-[#07539A]"}`}
              >
                <Filter size={14} /> Filter
                {activeFilterCount > 0 && <span className="w-5 h-5 rounded-full bg-[#07539A] !text-white text-[10px] flex items-center justify-center">{activeFilterCount}</span>}
              </button>

              {filterOpen && (
                <div className="absolute right-0 top-11 z-30 w-[min(92vw,34rem)] rounded-2xl border border-[#C9DCE6] bg-white shadow-[0_20px_50px_rgba(7,83,154,0.20)] p-4" data-testid="opportunities-filter-panel">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="font-display text-lg text-[#07539A]">Filter opportunities</div>
                      <div className="text-[10px] text-[#78909C] mt-0.5">Focus the deal table by stage, customer profile, and momentum.</div>
                    </div>
                    <button type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters" className="w-8 h-8 rounded-full hover:bg-[#EDF7FB] text-[#607D8B] flex items-center justify-center"><X size={14} /></button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <OpportunityFilterSelect label="Sales stage" value={gateFilter} onChange={setGateFilter} testId="opportunities-gate-filter" options={[
                      { key: "all", label: "All stages" },
                      ...GATES.map((gate, index) => ({ key: gate, label: `G${index + 1} · ${GATE_LABELS[gate]}` })),
                    ]} />
                    <OpportunityFilterSelect label="Industry" value={industryFilter} onChange={setIndustryFilter} testId="opportunities-industry-filter" options={[
                      { key: "all", label: "All industries" }, ...industries.map(value => ({ key: value, label: value })),
                    ]} />
                    <OpportunityFilterSelect label="Deal health" value={healthFilter} onChange={setHealthFilter} testId="opportunities-health-filter" options={[
                      { key: "all", label: "All health ratings" },
                      ...["excellent", "healthy", "watch", "at_risk"].map(value => ({ key: value, label: titleCase(value) })),
                    ]} />
                    <OpportunityFilterSelect label="Buyer engagement" value={temperatureFilter} onChange={setTemperatureFilter} testId="opportunities-temperature-filter" options={[
                      { key: "all", label: "All engagement levels" },
                      ...["burning", "hot", "warm", "cold"].map(value => ({ key: value, label: titleCase(value) })),
                    ]} />
                    <OpportunityFilterSelect label="Partner" value={partnerFilter} onChange={setPartnerFilter} testId="opportunities-partner-filter" options={[
                      { key: "all", label: "All partners" }, { key: "assigned", label: "Assigned" }, { key: "unassigned", label: "Unassigned" },
                      ...partners.map(value => ({ key: value, label: value })),
                    ]} />
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#E1EBF0] flex items-center justify-between">
                    <button type="button" onClick={clearFilters} className="text-[11px] text-[#607D8B] hover:text-[#07539A]">Clear all</button>
                    <button type="button" onClick={() => setFilterOpen(false)} className="h-9 px-4 rounded-xl bg-[#07539A] !text-white text-[12px] font-medium">Show {filtered.length} opportunities</button>
                  </div>
                </div>
              )}
              </div>
            </div>
          )}
        />

        {isPartner && <PartnerBenchmarks />}

        {viewMode === "funnel" ? (
          loading ? (
            <div className="p-10 text-center text-[13px] text-[#78909C]">Loading opportunities…</div>
          ) : loadError ? (
            <div className="p-10 text-center text-[13px] text-[#B42318]" data-testid="opps-load-error">{loadError}</div>
          ) : (
            <FunnelBoard
              opps={filtered}
              canMoveDeals={canEditOpp}
              moving={moving}
              onMove={moveGate}
              onPark={parkOpp}
              onParkRequest={setParkTarget}
              fromPath="/opportunities?view=funnel"
              fromLabel="Opportunities"
            />
          )
        ) : (
        <div className="border border-[#C9DCE6] rounded-[22px] overflow-hidden bg-white shadow-[0_10px_28px_rgba(7,83,154,0.08)]">
          <div className="overflow-x-auto">
            <div className="min-w-[970px]">
              <div className={`grid ${GRID_COLS} gap-2 items-center px-3 py-3 bg-[#F3FAFD] border-b border-[#D7E8F0] text-[10px] font-semibold text-[#07539A]`}>
                <div>Customer</div>
                <div className="text-center">Industry</div>
                <div>Interested products</div>
                <div className="text-center">Sales stage</div>
                <div className="text-center leading-tight">
                  <span className="block">value</span>
                  <span className="block text-[9px] font-normal opacity-80">{MONEY_UNIT_SHORT}</span>
                </div>
                <div className="text-center">Probability</div>
                <div className="text-center leading-tight">
                  <span className="block">Deal</span>
                  <span className="block">health</span>
                </div>
                <div className="text-center">Buyer engagement</div>
                <div className="text-center leading-tight">
                  <span className="block">Partner +</span>
                  <span className="block">Expected close</span>
                </div>
                <div />
              </div>

              {loading ? (
                <div className="p-10 text-center text-[13px] text-[#78909C]">Loading opportunities…</div>
              ) : loadError ? (
                <div className="p-10 text-center text-[13px] text-[#B42318]" data-testid="opps-load-error">{loadError}</div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center text-[13px] text-[#78909C]">No opportunities match your search and filters.</div>
              ) : (
                <div className="divide-y divide-[#E1EBF0]">
                  {filtered.map(opp => {
                    const gateIndex = GATES.indexOf(opp.current_gate);
                    const openDetail = () => nav(`/opportunities/${opp.id}`, { state: { from: "/opportunities", fromLabel: "Opportunities" } });
                    return (
                      <div
                        key={opp.id}
                        role="button"
                        tabIndex={0}
                        onClick={openDetail}
                        onKeyDown={event => { if (event.key === "Enter" || event.key === " ") openDetail(); }}
                        data-testid={`opp-card-${opp.id}`}
                        className={`group grid ${GRID_COLS} gap-2 items-center px-3 py-4 hover:bg-[#F8FCFE] transition-colors cursor-pointer`}
                      >
                        <div className="min-w-0 flex items-center gap-3">
                          <div className="w-8 h-8 rounded-sm bg-black/[0.05] border border-black/10 flex items-center justify-center text-[12px] text-[#526D7A] shrink-0">
                            {(opp.company?.name || "?").substring(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-[12px] font-medium text-[#455A64] truncate">{opp.company?.name || "Unknown customer"}</div>
                          </div>
                        </div>
                        <div className="text-center text-[12px] text-[#526D7A] truncate">{opp.company?.industry || "—"}</div>
                        <div className="min-w-0 flex flex-wrap gap-1">
                          {(opp.products || []).slice(0, 2).map(product => (
                            <span key={product} className="max-w-full truncate px-2 py-1 rounded-full bg-[#F3FAFD] border border-[#C9DCE6] text-[9px] text-[#526D7A]">{product}</span>
                          ))}
                          {(opp.products || []).length > 2 && <span className="px-2 py-1 rounded-full bg-[#E7F5FB] text-[9px] text-[#07539A]">+{opp.products.length - 2}</span>}
                          {!opp.products?.length && <span className="text-[11px] text-[#9AAAB3]">—</span>}
                        </div>
                        <div className="text-center">
                          <div className="text-[11px] font-medium text-[#07539A]">G{gateIndex + 1}</div>
                          <div className="text-[9px] text-[#78909C] truncate mt-0.5">{GATE_LABELS[opp.current_gate] || "—"}</div>
                        </div>
                        <div className="text-center text-[12px] text-[#455A64] tabular-nums">{formatLakh(opp.value_cr)}</div>
                        <div className="text-center text-[12px] text-[#526D7A] tabular-nums">{opp.probability ?? 0}%</div>
                        <div className="flex items-center justify-center">
                          <span className="text-[10px]" style={{ color: HEALTH_COLORS[opp.health] }}>{titleCase(opp.health)}</span>
                        </div>
                        <div className="flex items-center justify-center">
                          <span className="text-[10px]" style={{ color: TEMPERATURE_COLORS[opp.temperature] }}>{titleCase(opp.temperature)}</span>
                        </div>
                        <div className="min-w-0 text-center">
                          <div className="text-[11px] text-[#526D7A] truncate">{opp.partner?.firm || "Unassigned"}</div>
                          <div className="text-[9px] text-[#78909C] mt-1">Close {formatDate(opp.forecast_close_date)}</div>
                        </div>
                        <div className="flex items-center justify-end gap-1">
                          {canEditOpp && (
                            <button
                              type="button"
                              onClick={event => handleDeleteClick(opp, event)}
                              data-testid={`delete-opp-${opp.id}`}
                              title={isAdmin ? "Delete opportunity" : "Request deletion"}
                              className="w-6 h-6 rounded-lg text-[var(--danger)] hover:bg-[var(--danger)]/5 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100"
                            >
                              <Trash2 size={11} />
                            </button>
                          )}
                          <ChevronRight size={14} className="text-[#9AAAB3]" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      <RequestDeletionModal
        open={!!deletionTarget}
        onOpenChange={open => { if (!open) setDeletionTarget(null); }}
        entityType="opportunity"
        entityId={deletionTarget?.id}
        entityLabel={deletionTarget?.company?.name || deletionTarget?.name}
      />

      <ParkOpportunityModal
        open={Boolean(parkTarget)}
        opportunity={parkTarget}
        onOpenChange={(open) => !open && setParkTarget(null)}
        onParked={() => { setParkTarget(null); loadOpps(); }}
      />
    </div>
  );
}
